import { useRef, useEffect } from 'react';

interface UseAudioVisualizationProps {
  isUser: boolean;
}

export const useAudioVisualization = ({ isUser }: UseAudioVisualizationProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>();

  // Waveform drawing
  const drawWave = () => {
    if (!canvasRef.current || !analyserRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteTimeDomainData(dataArray);

    ctx.fillStyle = isUser ? 'rgba(34, 211, 238, 0.05)' : 'rgba(148, 163, 184, 0.05)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.lineWidth = 2;
    ctx.strokeStyle = isUser ? '#22d3ee' : '#94a3b8';
    ctx.beginPath();

    const sliceWidth = canvas.width / dataArray.length;
    let x = 0;

    for (let i = 0; i < dataArray.length; i++) {
      const v = dataArray[i] / 128.0;
      const y = (v * canvas.height) / 2;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      x += sliceWidth;
    }

    ctx.stroke();

    if (!audioRef.current?.paused) {
      animationFrameRef.current = requestAnimationFrame(drawWave);
    }
  };

  // Audio visualization setup
  const setupAudioVisualization = async (audioElement: HTMLAudioElement) => {
    try {
      if (audioContextRef.current) {
        await audioContextRef.current.close();
      }

      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaElementSource(audioElement);
      const analyser = audioContextRef.current.createAnalyser();
      
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      
      source.connect(analyser);
      analyser.connect(audioContextRef.current.destination);
      analyserRef.current = analyser;

      drawWave();
    } catch (error) {
      console.error('Audio visualization error:', error);
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    }
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return {
    canvasRef,
    audioRef,
    setupAudioVisualization
  };
}; 